import React, { useState, useEffect } from 'react';
import { axios } from "@/lib/axios";
import { Link, matchPath, useLocation, useNavigate, useParams } from 'react-router-dom';
import { User, useUserStore } from "@/stores/user";
import { Project, ProjectCategory, AdminCategory, useProjectStore } from "@/stores/project";
import {useProgressStore} from "@/stores/progress";
import { useLoadingStore } from '@/stores/loading'
import {useMessageModalStore} from '@/stores/message'
import qs from 'qs'
import { getProjects, getAdminCategories } from "@/api/projects";
import { FaBars, FaChevronDown, FaChevronLeft } from 'react-icons/fa';
import * as Config from '@/config'
import {useAdmin} from "@/containers/admin/useAdmin";
import { useBlockerStore } from '@/stores/blocker';
import {ConfirmModal, ConfirmModalProps} from '@/components/ConfirmModal';
import {useCustomerExcelDownload} from "@/utils/useCustomerExcelDownload";

const SideMenu = () => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const [isBlocker, setPendingAction] = useBlockerStore(state => [
    state.isBlocker,
    state.setPendingAction
  ]);

  const dashboardParams = matchPath("/dashboard/:projectCategoryId", pathname)?.params || null;
  const progressParams = matchPath("/progress/:projectCategoryId/*", pathname)?.params || null;
  const query = qs.parse(search.substring(1));

  const setLoading = useLoadingStore(state => state.setLoading);
  const setMessage = useMessageModalStore(state => state.setMessage);

  const user = useUserStore(state => state.user);
  const { ADMIN_CONTENTS } = useAdmin(navigate, user);
  const adminMap = new Map(ADMIN_CONTENTS.map(item => [item.id, item]));

  const [confirmModal, setConfirmModal] = useState<ConfirmModalProps | undefined>(undefined);  
  const {excelDownload} = useCustomerExcelDownload();

  const [
    selectedProject, 
    setSelectedProject, 
    selectedProjectCategory, 
    setSelectedProjectCategory,
    selectedSubProjectCategory, 
    setSelectedSubProjectCategory,
    selectedAdminCategory, 
    setSelectedAdminCategory,
    isMenuOpen, 
    setIsMenuOpen
  ] = useProjectStore(state => [
    state.project,
    state.setProject,
    state.projectCategory,
    state.setProjectCategory,
    state.subProjectCategory,
    state.setSubProjectCategory,
    state.adminCategory,
    state.setAdminCategory,
    state.isMenuOpen,
    state.setIsMenuOpen,
  ]);
  const [setServiceStartInfoType] = useProgressStore(state => [state.setServiceStartInfoType])
  const [openProjects, setOpenProjects] = useState<number[]>([]);
  const [openAdminProjects, setOpenAdminProjects] = useState<string[]>([]);
  const [isHoverOpen, setIsHoverOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectCategories, setProjectCategories] = useState<ProjectCategory[]>([]);
  const [adminProjects, setAdminProjects] = useState<Project[]>([]);

  const setInit = (
    project: Project[] = [],
    projectCategories: ProjectCategory[] = [],
    selectedProject: Project | undefined = undefined,
    selectedProjectCategory: ProjectCategory | undefined = undefined
  ) => {
    setProjects(project);
    setProjectCategories(projectCategories);
    setSelectedProject(selectedProject);
    setSelectedProjectCategory(selectedProjectCategory);
  }

  function apiProjects( user: User ) {
    setLoading(true);
    getProjects().then(result => {
      const projects = result.data.projects.filter((p:Project)=>p.id !== Config.PROJECT_ID_BILLING) as Array<Project>;
      let projectCategories: ProjectCategory[] = [], initProject = undefined, initProjectCategory = undefined;
      let urlParam = progressParams?.projectCategoryId ?? dashboardParams?.projectCategoryId ?? query?.project_category_id as string;
      if (!urlParam) {
        for (const path in Config.PROJECT_CATEGORY_PATH) {
          if (matchPath(path, pathname)) {
            urlParam = Config.PROJECT_CATEGORY_PATH[path as keyof typeof Config.PROJECT_CATEGORY_PATH].toString();
            break;
          }
        }
      }
      if (urlParam) {
        loop:
          for (const project of projects) {
            for (const projectCategory of project.project_categories) {
              if (projectCategory.id === parseInt(urlParam)) {
                initProject = project;
                projectCategories = project.project_categories;
                initProjectCategory = projectCategory;
                break loop;
              }
            }
          }
      }
      
      if (initProject) {
        setOpenProjects([initProject.id]);
        setSelectedProject(initProject);
        setSelectedProjectCategory(initProjectCategory);
      }
      setProjects(projects);
      setProjectCategories(projectCategories);
    }).catch(error => {
    }).finally(() => {
      setLoading(false);
    });
  }

  function apiAdminCategories( user: User ) {
    setLoading(true);
    getAdminCategories().then(result => {   
      const rawAdminProjects: Project[] = result.data.projects;

      let initProject = undefined;
      let initAdminCategory = undefined;
      const viewableAdminProjects = rawAdminProjects.map(project => {
        const viewableCategories = project.admin_categories?.filter(category => {
          const admin = adminMap.get(category.id);
          if (admin?.showContent && matchPath(admin.path, pathname)) {
            initProject = project;
            initAdminCategory = category;
          } 
          return admin?.showContent === true
        }) ?? [];
        return { ...project, admin_categories: viewableCategories };
      }).filter(project => project.admin_categories.length > 0);

      if (initProject) {
        setSelectedProject(initProject);
        setSelectedAdminCategory(initAdminCategory);
      }

      setAdminProjects(viewableAdminProjects);

    }).catch(error => {
    }).finally(() => {
      setLoading(false);
    });
  }
  
  useEffect(() => {
    if (user) {
      apiProjects(user);
      apiAdminCategories(user);
    } else {
      setInit();
    }
    return () => {
    };
  }, [user]);

  const toggleProject = (projectId: number) => {
    setOpenProjects((prev) =>
      prev.includes(projectId) ? prev.filter((id) => id !== projectId) : [...prev, projectId]
    );
  };

  const toggleAdminProject = (projectName: string) => {
    setOpenAdminProjects((prev) =>
      prev.includes(projectName) ? prev.filter((name) => name !== projectName) : [...prev, projectName]
    );
  };

  const confirmOrRun = (nextPath: string | null | undefined, effect: () => void) => {
    if (isBlocker && nextPath) {
      setPendingAction(effect);
      navigate(nextPath);
      return;
    }
    effect();
    if (nextPath) navigate(nextPath);
  };

  function selectProject (
    project: Project | undefined, 
    category: ProjectCategory | undefined, 
    subProjectCategory: ProjectCategory | undefined = undefined ,
    adminCategory: AdminCategory | undefined = undefined,
  ) {
      setSelectedProject(project);
      setSelectedProjectCategory(category);
      setSelectedSubProjectCategory(subProjectCategory);
      setSelectedAdminCategory(adminCategory);
  }

  
  const onClickProjectManagement = (project: Project, category: ProjectCategory) => {
    if (Config.DASHBOARD_CATEGORY_IDS.includes(category.id)) {
      const effect = () => {
        selectProject(project, category);
      };
      confirmOrRun(`/dashboard/${project.id}`, effect);
      return;
    }
    if (category.id === Config.PROJECT_CATEGORY_ID_REPORT_OUTPUT) {
      const effect = () => {
        selectProject(project, category);
      };
      confirmOrRun('/report', effect);
      return;
    }
    if (category.id === Config.PROJECT_CATEGORY_ID_CUSTOMER_LIST) {
      const effect = () => {
        selectProject(project, category);
      };
      confirmOrRun('/customer', effect);
      return;
    }
    if (category.id === Config.PROJECT_CATEGORY_ID_CUSTOMER_LIST_DOWNLOAD) {
      const effect = () => {
        if (matchPath("/customer", pathname)) {
          setSelectedSubProjectCategory(category);
        } else {
          setConfirmModal({
            text: "顧客一覧が表示されていません。検索による絞り込みがありませんが、全件出力してよろしいでしょうか？",
            confirmButtonText: "OK",
            isShow: true,
            onCancel: () => {setConfirmModal(undefined)},
            onConfirm: () => {
              console.log("download");
              excelDownload({});
              setConfirmModal(undefined);
            },
          })
        }
      };
      confirmOrRun(null, effect);
      return;
    }

    if (category.id === Config.PROJECT_CATEGORY_ID_API_HISTORY) {
      const effect = () => {
        selectProject(project, category);
      };
      confirmOrRun('/customer/apiUsage', effect);
      return;
    }

    if (category.id === Config.PROJECT_CATEGORY_ID_PF_NEWS) {
      const effect = () => {
        selectProject(project, category);
      };
      confirmOrRun('/admin/portal', effect);
      return;
    }

    if (Config.GEOJSON_DOWNLOAD_CATEGORY_IDS.includes(category.id)) {
      const effect = () => {
        if (matchPath("/progress", pathname) && selectedProject?.id === project.id) {
          setSelectedSubProjectCategory(category);
        } else if (Config.PROJECT_CATEGORY_ID_NAVI_GEOJSON_DOWNLOAD === category.id) {
          setMessage("カーナビ要望にチェックが入っていません。");
        } else if (Config.PROJECT_CATEGORY_ID_SERVICE_START_GEOJSON_DOWNLOAD === category.id) {
          setMessage("供用開始情報にチェックが入っていません。");
        }
      };
      confirmOrRun(null, effect);
      return;
    }

    if (category.id === Config.PROJECT_CATEGORY_ID_SERVICE_START_MANAGEMENT_LIST) {
      const effect = () => {
        setServiceStartInfoType(Config.ServiceStartInformationList.manage);
        const startInfo = project.project_categories.find(pc => pc.id === Config.PROJECT_CATEGORY_ID_SERVICE_START_INFO);
        selectProject(project, startInfo, category);
      };
      const nextPath = matchPath("/progress", pathname) ? null : '/progress';
      confirmOrRun(nextPath, effect);
      return;
    }

    if (category.id === Config.PROJECT_CATEGORY_ID_MATERIAL_COLLECTION_LIST) {
      const effect = () => {
        setServiceStartInfoType(Config.ServiceStartInformationList.collect);
        const startInfo = project.project_categories.find(pc => pc.id === Config.PROJECT_CATEGORY_ID_SERVICE_START_INFO);
        selectProject(project, startInfo, category);
      };
      const nextPath = matchPath("/progress", pathname) ? null : '/progress';
      confirmOrRun(nextPath, effect);
      return;
    }

    const effect = () => {
      if (category.id === Config.PROJECT_CATEGORY_ID_SERVICE_START_INFO) {
        setServiceStartInfoType(Config.ServiceStartInformationList.progress);
      }
      selectProject(project, category);
    };
    const nextPath = matchPath("/progress", pathname) ? null : '/progress';
    confirmOrRun(nextPath, effect);
  };

  const onClickSystemManagement = (project: Project, category: AdminCategory) => {
    const next = adminMap.get(category.id)?.path || '#';
    const effect = () => {
      selectProject(project, undefined, undefined, category);
    };
    confirmOrRun(next === '#' ? null : next, effect);
  };

  const handleMouseOver = () => {
    if (!isMenuOpen) {
      setIsHoverOpen(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHoverOpen(false);
  };

  const menuClass = isMenuOpen || isHoverOpen ? 'side-menu open' : 'side-menu';

  const renderProjectMenu = () => (
    <div className="menu-section">
      <ul>
        <li>
          <div className="menu-item title">
            <span className="bullet">▶</span>
            <div className="hide-label">プロジェクト管理</div>
          </div>
        </li>
        {projects.map((project) => (
          <li key={project.id}>
            <div className="menu-item parent" onClick={() => toggleProject(project.id)}>
              <span className="bullet">◇</span>
              <div className="hide-label label-container">
                <span>{project.name}</span>
                {openProjects.includes(project.id) ? <FaChevronDown /> : <FaChevronLeft />}
              </div>
            </div>

            <ul className={`sub-menu hide-label ${openProjects.includes(project.id) ? 'is-open' : ''}`}>
              {project.project_categories?.map((category) => (
                <li 
                  key={category.id}
                  // className={pathname.includes(`/projects/${project.id}/categories/${category.id}`) ? 'active' : ''}
                >
                  <a onClick={() => onClickProjectManagement(project, category)}>{category.name}</a>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );

  
  const renderAdminMenu = () => (
    <div className="menu-section">
      <ul>
        <li>
          <div className="menu-item title">
            <span className="bullet">▶</span>
            <div className="hide-label">システム管理</div>
          </div>
        </li>
        {adminProjects.map((adminProject) => (
          <li key={adminProject.name}>
            <div className="menu-item parent" onClick={() => toggleAdminProject(adminProject.name)}>
              <span className="bullet">◇</span>
              <div className="hide-label label-container">
                <span>{adminProject.name}</span>
                {openAdminProjects.includes(adminProject.name) ? <FaChevronDown /> : <FaChevronLeft />}
              </div>
            </div>
            <ul className={`sub-menu hide-label ${openAdminProjects.includes(adminProject.name) ? 'is-open' : ''}`}>
              {(adminProject.admin_categories as AdminCategory[]).map((category: AdminCategory) => (
                <li 
                  key={category.id} 
                  // className={pathname === adminMap.get(category.id)?.path ? 'active' : ''}
                  >
                  <a onClick={() => onClickSystemManagement(adminProject, category)}>{category.name}</a>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );


  return (
    <>
      <nav className={menuClass} onMouseOver={handleMouseOver} onMouseLeave={handleMouseLeave}>
        <div className="menu-section">
          <ul>
            <li>
              <Link to='/top'>
                <div className="menu-item title">
                  <span className="bullet">▶</span>
                  <div className="hide-label">お知らせ</div>
                </div>
              </Link>
            </li>
          </ul>
        </div>
        {projects.length > 0 && renderProjectMenu()}
        {adminProjects.length > 0 && renderAdminMenu()}
      </nav>
      {confirmModal && 
        <ConfirmModal {...confirmModal} />
      }
    </>
  );
};

export default SideMenu;